/**
 * Dropzone — a click-to-browse / drag-and-drop file picker. Pure (no network):
 * it validates against `accept` (a list of MIME types or extensions) and calls
 * `onFiles(File[])` with the accepted files. Shows a distinct drag-over state.
 *
 *   <Dropzone
 *     accept={["image/png", "image/jpeg"]}
 *     multiple
 *     onFiles={(files) => upload(files)}
 *   />
 */
import {
  forwardRef,
  useCallback,
  useId,
  useRef,
  useState,
  type DragEvent,
  type ReactNode,
} from "react";
import { CloudUpload } from "lucide-react";
import { useTr, type Entry } from "../i18n/context.js";

const S = {
  defaultTitle: { zh: "拖拽文件到这里，或点击上传", en: "Drag files here, or click to upload" },
} satisfies Record<string, Entry>;

export interface DropzoneProps {
  /** Called with the accepted files (after filtering by `accept`). */
  readonly onFiles: (files: File[]) => void;
  /** Allowed MIME types and/or extensions, e.g. ["image/png", ".webp"]. */
  readonly accept?: readonly string[];
  /** Allow selecting more than one file. @default false */
  readonly multiple?: boolean;
  /** Disable interaction. */
  readonly disabled?: boolean;
  /** Title line. Defaults to a localized "drag files here, or click to upload". */
  readonly title?: ReactNode;
  /** Hint line under the title (e.g. accepted formats). */
  readonly hint?: ReactNode;
  /** Custom icon (defaults to a cloud-upload glyph). */
  readonly icon?: ReactNode;
  readonly className?: string;
  readonly "aria-label"?: string;
}

/** Returns true when a file matches one of the accept patterns. */
function matches(file: File, accept?: readonly string[]): boolean {
  if (!accept || accept.length === 0) return true;
  const name = file.name.toLowerCase();
  const type = file.type.toLowerCase();
  return accept.some((raw) => {
    const pattern = raw.trim().toLowerCase();
    if (pattern.startsWith(".")) return name.endsWith(pattern);
    if (pattern.endsWith("/*")) return type.startsWith(pattern.slice(0, -1));
    return type === pattern;
  });
}

export const Dropzone = forwardRef<HTMLDivElement, DropzoneProps>(function Dropzone(
  {
    onFiles,
    accept,
    multiple = false,
    disabled = false,
    title,
    hint,
    icon,
    className,
    ...rest
  },
  ref,
) {
  const tr = useTr();
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const labelId = useId();
  const resolvedTitle = title ?? tr(S.defaultTitle);

  const handleFiles = useCallback(
    (list: FileList | null) => {
      if (!list) return;
      const accepted = Array.from(list).filter((f) => matches(f, accept));
      if (accepted.length > 0) onFiles(multiple ? accepted : accepted.slice(0, 1));
    },
    [accept, multiple, onFiles],
  );

  const openBrowser = useCallback(() => {
    if (!disabled) inputRef.current?.click();
  }, [disabled]);

  const onDrop = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setDragging(false);
      if (disabled) return;
      handleFiles(e.dataTransfer.files);
    },
    [disabled, handleFiles],
  );

  const onDragOver = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      if (!disabled) setDragging(true);
    },
    [disabled],
  );

  return (
    <div
      ref={ref}
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-disabled={disabled || undefined}
      aria-labelledby={labelId}
      aria-label={rest["aria-label"]}
      className={["lc-dropzone", className].filter(Boolean).join(" ")}
      data-dragging={dragging || undefined}
      data-disabled={disabled || undefined}
      onClick={openBrowser}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          openBrowser();
        }
      }}
      onDragOver={onDragOver}
      onDragLeave={() => setDragging(false)}
      onDrop={onDrop}
    >
      <span className="lc-dropzone__icon" aria-hidden="true">
        {icon ?? <CloudUpload size={28} />}
      </span>
      <span id={labelId} className="lc-dropzone__title">
        {resolvedTitle}
      </span>
      {hint != null ? <span className="lc-dropzone__hint">{hint}</span> : null}
      <input
        ref={inputRef}
        type="file"
        className="lc-dropzone__input"
        accept={accept?.join(",")}
        multiple={multiple}
        disabled={disabled}
        tabIndex={-1}
        onChange={(e) => {
          handleFiles(e.target.files);
          e.target.value = "";
        }}
      />
    </div>
  );
});
